import React, { useEffect, useRef, useState, useMemo, useContext } from "react";
import { Helmet } from "react-helmet";
import { useParams, Link } from "react-router-dom";
import { blogData } from "../utils/blogData";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import ScrollToTopButton from "../components/ScrollTop";
import SEO from "../components/SEO";
import WhatsAppMsg from "../components/WhatsApp";
import { addBlogComment } from "../utils/blogComments";
import { getBlogDetails } from "../utils/getBlogDetails";
import { blogLike } from "../utils/likeBlogs"; // Your provided API function
import { formatCommentDate } from "../utils/formatDate";
import { UserContext } from "../utils/UserContext";
import { FiShare } from "react-icons/fi";
import { IoIosLink } from "react-icons/io";
import { FaXTwitter } from "react-icons/fa6";
import { FaFacebook, FaLinkedin } from "react-icons/fa";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  AiOutlineLike,
  AiOutlineDislike,
  AiFillLike,
  AiFillDislike,
  AiOutlineComment,
} from "react-icons/ai";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateDDMMMYYYY } from "../utils/dateHelpers";

const BlogDetails = () => {
  const { title } = useParams();
  const { user, openSignInModal } = useContext(UserContext);

  // New state to hold fetched blogs and the matched blog
  const [blogs, setBlogs] = useState([]);
  const [blog, setBlog] = useState(null);
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  // Fetch all blogs on component mount
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const data = await blogData();
        setBlogs(data);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoadingBlogs(false);
      }
    };
    fetchBlogs();
  }, []);

  // Once blogs are fetched, find the blog with matching slug
  useEffect(() => {
    if (blogs.length > 0) {
      const foundBlog = blogs.find((b) => b.slug === title);
      // Map blogId to id if necessary
      setBlog(
        foundBlog
          ? { ...foundBlog, id: foundBlog.blogId || foundBlog.id }
          : null
      );
    }
  }, [blogs, title]);

  // Calculate current blog index, previous and next blogs
  const blogIndex = blogs.findIndex((b) => b.slug === title);
  const prevBlog = blogIndex > 0 ? blogs[blogIndex - 1] : null;
  const nextBlog = blogIndex < blogs.length - 1 ? blogs[blogIndex + 1] : null;

  // Function to get two random blogs (excluding the current blog)
  const getRandomTwoBlogs = () => {
    const filteredBlogs = blogs.filter((b) => b.slug !== title);
    if (filteredBlogs.length <= 2) {
      return filteredBlogs;
    }
    const selectedBlogs = [];
    const usedIndices = new Set();
    while (selectedBlogs.length < 2) {
      const randomIndex = Math.floor(Math.random() * filteredBlogs.length);
      if (!usedIndices.has(randomIndex)) {
        usedIndices.add(randomIndex);
        selectedBlogs.push(filteredBlogs[randomIndex]);
      }
    }
    return selectedBlogs;
  };

  // Memoize the latestTwoBlogs to avoid re-selection on every render
  // eslint-disable-next-line
  const latestTwoBlogs = useMemo(() => getRandomTwoBlogs(), [blogs, title]);

  // Other state and ref declarations remain unchanged
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showEbookBanner, setShowEbookBanner] = useState(false);
  const ctaRef = useRef(null);
  // Initialize username based on user authentication
  const [username, setUsername] = useState(user ? user.name : "");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // States for fetched comments
  const [comments, setComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState(null);
  // Add a state variable to control the Book Demo modal:
  const [openBookDemoModal, setOpenBookDemoModal] = useState(false);
  // States for likes and dislikes
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [isLikeActive, setIsLikeActive] = useState(false);
  const [isDislikeActive, setIsDislikeActive] = useState(false);
  const [isProcessingLike, setIsProcessingLike] = useState(false);
  const [isProcessingDislike, setIsProcessingDislike] = useState(false);

  // **New State Variables for Animation**
  const [likeAnimation, setLikeAnimation] = useState(false);
  const [dislikeAnimation, setDislikeAnimation] = useState(false);

  // **New State Variable for Comment Section Visibility**
  const [showComments, setShowComments] = useState(true);

  // Generate current date in YYYY-MM-DD format
  const currentDate = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY >= 200) {
        setShowEbookBanner(true);
      } else {
        setShowEbookBanner(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Updated function: if the user is not logged in, open the sign-in modal
  const handleEbookClick = () => {
    if (!user) {
      openSignInModal();
      return;
    }
    // If the user is logged in, trigger the download
    window.open(
      "https://content.qeeb.in/coltec/A-BEGINNER'S-GUIDEBOOK-TO-BUYING-IFPD.pdf",
      "_blank"
    );
  };

  // Fetch comments, likes, and dislikes when the blog changes
  useEffect(() => {
    const fetchBlogDetails = async () => {
      if (!blog) return;

      setIsLoadingComments(true);
      setCommentsError(null);

      try {
        const blogDetails = await getBlogDetails(blog.id);
        // Sort comments in descending order based on createdAt
        const sortedComments = (blogDetails.comments || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setComments(sortedComments);
        setLikes(blogDetails.likes || 0);
        setDislikes(blogDetails.dislikes || 0);
        // Set active states based on blogDetails if needed
        // Assuming blogDetails does not provide user-specific like/dislike status
        setIsLikeActive(false);
        setIsDislikeActive(false);
      } catch (error) {
        setCommentsError(error.message || "Failed to load comments.");
      } finally {
        setIsLoadingComments(false);
      }
    };

    fetchBlogDetails();
  }, [blog]);

  // Update username if user changes (e.g., signs in/out)
  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      setUsername(user.name);
    } else {
      // If user is falsy, try to load from localStorage instead of removing it.
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUsername(parsedUser.name);
      }
    }
  }, [user]);

  // Intersection Observer to detect when CTA is visible
  useEffect(() => {
    const targetNode = ctaRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowEbookBanner(entry.isIntersecting);
      },
      { threshold: 0.8 }
    );

    if (targetNode) {
      observer.observe(targetNode);
    }

    return () => {
      if (targetNode) {
        observer.unobserve(targetNode);
      }
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const toggleComments = () => {
    setShowComments((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      openSignInModal();
      toast.info("Please sign in to post a comment.", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    setIsSubmitting(true);
    const commentUsername = user ? user.name : username;

    try {
      await addBlogComment(
        blog.id,
        commentUsername,
        comment,
        blog.title,
        blog.category
      );
      toast.success("Comment posted successfully!", {
        position: "top-right",
        autoClose: 2000, // 2 seconds
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
      setUsername("");
      setComment("");
      // Refresh comments after successful submission
      const blogDetails = await getBlogDetails(blog.id);
      setComments(
        (blogDetails.comments || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
      );
      setLikes(blogDetails.likes || 0);
      setDislikes(blogDetails.dislikes || 0);
      setIsLikeActive(false);
      setIsDislikeActive(false);
    } catch (error) {
      toast.error(error.message || "Failed to post comment.", {
        position: "top-right",
        autoClose: 2000, // 2 seconds
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async () => {
    if (isProcessingLike) return;
    setIsProcessingLike(true);
    setLikeAnimation(true);
    setLikes((prev) => prev + 1);
    setIsLikeActive(true);

    try {
      await blogLike(blog.id, "like");
    } catch (error) {
      console.error("Failed to update like:", error.message);
      setLikes((prev) => prev - 1);
      setIsLikeActive(false);
    } finally {
      setIsProcessingLike(false);
      setTimeout(() => {
        setLikeAnimation(false);
      }, 750);
    }
  };

  const handleDislike = async () => {
    if (isProcessingDislike) return;
    setIsProcessingDislike(true);
    setDislikeAnimation(true);
    setDislikes((prev) => prev + 1);
    setIsDislikeActive(true);

    try {
      await blogLike(blog.id, "dislike");
    } catch (error) {
      console.error("Failed to update dislike:", error.message);
      setDislikes((prev) => prev - 1);
      setIsDislikeActive(false);
    } finally {
      setIsProcessingDislike(false);
      setTimeout(() => {
        setDislikeAnimation(false);
      }, 750);
    }
  };

  // Link sharing handlers remain unchanged
  const handleCopyLink = () => {
    const blogUrl = window.location.href;
    navigator.clipboard
      .writeText(blogUrl)
      .then(() => {
        toast.success("Link copied to clipboard!", {
          position: "top-right",
          autoClose: 2000,
        });
      })
      .catch((error) => {
        toast.error("Failed to copy link.", {
          position: "top-right",
          autoClose: 2000,
        });
        console.error("Error copying link: ", error);
      });
    setIsDropdownOpen(false);
  };

  const handleShareOnX = () => {
    const blogUrl = encodeURIComponent(window.location.href);
    const tweetText = `${blog.title}\n${blogUrl}`;
    const xShareUrl = `https://x.com/intent/tweet?text=${tweetText}`;
    const windowFeatures =
      "width=600,height=500,resizable=yes,scrollbars=yes,toolbar=no,menubar=no";
    window.open(xShareUrl, "shareWindow", windowFeatures);
    setIsDropdownOpen(false);
  };

  const handleShareOnFacebook = () => {
    const blogUrl = encodeURIComponent(window.location.href);
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${blogUrl}&title=${blog.title}`;
    const windowFeatures =
      "width=600,height=500,resizable=yes,scrollbars=yes,toolbar=no,menubar=no";
    window.open(facebookShareUrl, "shareWindow", windowFeatures);
    setIsDropdownOpen(false);
  };

  const handleShareOnLinkedIn = () => {
    const blogUrl = encodeURIComponent(window.location.href);
    const linkedinShareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${blogUrl}&title=${blog.title}`;
    const windowFeatures =
      "width=600,height=500,resizable=yes,scrollbars=yes,toolbar=no,menubar=no";
    window.open(linkedinShareUrl, "shareWindow", windowFeatures);
    setIsDropdownOpen(false);
  };

  // Display a loading message while fetching blogs
  if (loadingBlogs) {
    return (
      <div className="flex justify-center items-center h-[100vh]">
        <div className="spin-loader"></div>
      </div>
    );
  }

  return (
    <div className="blogs-page">
      {blog ? (
        <>
          {/** SEO Meta Tags & JSON‑LD Structured Data **/}
          <SEO
            title={`${blog.title} | Coltec Global`}
            description={blog.meta_description}
            keywords={blog.keywords}
            ogTitle={blog.title}
            ogDescription={blog.meta_description}
            ogImage={blog.blog_image}
            ogUrl={`https://coltec-global.com/blogs/${blog.slug}`}
            canonicalUrl={`https://coltec-global.com/blogs/${blog.slug}`}
            ogSiteName="Coltec Global"
            ogType="website"
            twitterSite="https://x.com/ColtecGlobal"
            twitterTitle={blog.title}
            twitterDescription={blog.meta_description}
            twitterImage={blog.blog_image}
            twitterCard="summary"
            geoRegion="IN"
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify(
                {
                  "@context": "https://schema.org",
                  "@graph": [
                    {
                      "@type": "Organization",
                      "@id": "https://coltec-global.com/#organization",
                      name: "COLTEC Global",
                      aggregateRating: {
                        "@type": "AggregateRating",
                        ratingValue: "4.8",
                        reviewCount: "350",
                      },
                      description:
                        "COLTEC Global is a leading manufacturer of Visual Interactive Display Technology, delivering innovative solutions for Education, Corporate, and Retail applications.",
                      url: "https://coltec-global.com",
                      logo: {
                        "@type": "ImageObject",
                        url: "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                      },
                      address: [
                        {
                          "@type": "PostalAddress",
                          streetAddress:
                            "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                          addressLocality: "Hyderabad",
                          addressRegion: "Telangana",
                          postalCode: "500030",
                          addressCountry: "IN",
                        },
                        {
                          "@type": "PostalAddress",
                          streetAddress:
                            "COLTEC GLOBAL #41-14-8/2, Opp Mouli Towers, Near Jyothi Convention Hall,Benz circle",
                          addressLocality: "Vijayawada",
                          addressRegion: "Andhra Pradesh",
                          postalCode: "520 010",
                          addressCountry: "IN",
                        },
                      ],
                      sameAs: [
                        "https://www.facebook.com/profile.php?id=61570213181020",
                        "https://x.com/ColtecGlobal",
                        "https://www.linkedin.com/company/coltec-global/",
                        "https://www.youtube.com/@coltecglobal5339",
                        "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z",
                      ],
                      email: "sales@coltec-global.com",
                      contactPoint: [
                        {
                          "@type": "ContactPoint",
                          telephone: "+91 1800 309 8822",
                          contactType: "customer support",
                        },
                      ],
                    },
                    {
                      "@type": "WebSite",
                      "@id": "https://coltec-global.com/#website",
                      url: "https://coltec-global.com",
                      name: "COLTEC Global",
                      publisher: {
                        "@id": "https://coltec-global.com/#organization",
                      },
                      potentialAction: {
                        "@type": "SearchAction",
                        target: `https://coltec-global.com/blogs/${blog.slug}?s={search_term_string}`,
                        "query-input": "required name=search_term_string",
                      },
                      inLanguage: "en",
                    },
                    {
                      "@type": "Blog",
                      name: "Educational & Corporate Technology Insights | Coltec Blogs",
                      url: "https://coltec-global.com/blogs",
                      blogPost: [
                        {
                          "@type": "BlogPosting",
                          headline: blog.title,
                          url: `https://coltec-global.com/blogs/${blog.slug}`,
                          datePublished: formatDateDDMMMYYYY(blog.created_at),
                          dateModified: currentDate,
                          author: {
                            "@type": "Person",
                            name: blog.author_name,
                          },
                          aggregateRating: {
                            "@type": "AggregateRating",
                            ratingValue: "4.5",
                            reviewCount: "500",
                          },
                        },
                      ],
                    },
                    {
                      "@type": "BreadcrumbList",
                      itemListElement: [
                        {
                          "@type": "ListItem",
                          position: 1,
                          name: "Home",
                          item: "https://coltec-global.com",
                        },
                        {
                          "@type": "ListItem",
                          position: 2,
                          name: "Blogs",
                          item: "https://coltec-global.com/blogs",
                        },
                        {
                          "@type": "ListItem",
                          position: 3,
                          name: blog.title,
                          item: `https://coltec-global.com/blogs/${blog.slug}`,
                        },
                      ],
                    },
                    ...blogs.map((t) => ({
                      "@type": "Review",
                      headline: blog.title,
                      itemReviewed: {
                        "@type": "Product",
                        name: blog.title,
                      },
                      author: {
                        "@type": "Person",
                        name: t.author_name,
                      },
                      reviewBody: t.meta_description,
                      reviewRating: {
                        "@type": "Rating",
                        ratingValue: "4.5",
                        bestRating: "5",
                        worstRating: "1",
                      },
                      datePublished: formatDateDDMMMYYYY(t.created_at),
                      dateModified: currentDate,
                      publisher: {
                        "@type": "Organization",
                        name: "COLTEC Global",
                      },
                    })),
                    {
                      "@type": "LocalBusiness",
                      "@id": "https://coltec-global.com/#localbusiness",
                      name: "COLTEC Global",
                      url: "https://coltec-global.com",
                      image:
                        "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                      address: {
                        "@type": "PostalAddress",
                        streetAddress:
                          "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                        addressLocality: "Hyderabad",
                        addressRegion: "Telangana",
                        postalCode: "500030",
                        addressCountry: "IN",
                      },
                      telephone: "+91 1800 309 8822",
                      openingHours: "Mon-Sat 09:00-6:00",
                      geo: {
                        "@type": "GeoCoordinates",
                        latitude: "17.3297791",
                        longitude: "78.4381149",
                      },
                      sameAs: [
                        "https://www.facebook.com/profile.php?id=61570213181020",
                        "https://x.com/ColtecGlobal",
                        "https://www.linkedin.com/company/coltec-global/",
                        "https://www.youtube.com/@coltecglobal5339",
                        "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z",
                      ],
                    },
                  ],
                },
                null,
                2
              )}
            </script>
          </Helmet>
          <Header />
          <div className="relative">
            <div className="mx-auto max-w-7xl px-6 pt-24 pb-12 lg:px-8 md:pt-28 md:pb-14">
              <div className="grid grid-cols-12 xl:gap-x-10 gap-6 min-h-screen">
                <div className="xl:col-span-8 col-span-full relative">
                  <div className="mb-12 relative">
                    <Link to="/blogs">
                      <img
                        alt="Blogs Arrow"
                        src="https://content.qeeb.in/coltec/arrow-black.svg"
                        className="w-6 absolute top-[-26px] xl:top-0 xl:left-[-30px] xl:pt-2.5"
                      />
                    </Link>
                    <div className="flex space-x-2">
                      <h1 className="md:text-[30px] text-[22px] leading-8 font-semibold mb-2.5 max-w-2xl">
                        {blog.title}
                      </h1>
                    </div>
                    <div className="flex items-center">
                      <p className="font-medium text-sm mr-3">
                        <span className="text-gray-900">
                          {blog.author_name}
                        </span>
                      </p>
                      <p className="text-gray-500">
                        <span className="text-sm">
                          {formatDateDDMMMYYYY(blog.created_at)}
                        </span>
                      </p>
                      <div className="md:block hidden">
                        <div className="flex justify-start items-center ml-3">
                          <div>
                            <img
                              alt="Category"
                              src="https://content.qeeb.in/coltec/category-icon.svg"
                              className="h-5"
                            />
                          </div>
                          <Link
                            to={blog.category_url}
                            className="bg-white border border-[#242424] ml-3 text-[#000] hover:text-[#242424] py-1 px-3 rounded-full text-sm"
                          >
                            {blog.category}
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="md:hidden block">
                      <div className="flex justify-start items-center mt-3">
                        <div>
                          <img
                            alt="Category"
                            src="https://content.qeeb.in/coltec/category-icon.svg"
                            className="h-5"
                          />
                        </div>
                        <Link
                          to={blog.category_url}
                          className="bg-white border border-[#242424] ml-3 text-[#000] hover:text-[#242424] py-1 px-3 rounded-full text-sm"
                        >
                          {blog.category}
                        </Link>
                      </div>
                    </div>
                  </div>
                  <img
                    alt={blog.blog_image_alt}
                    src={blog.blog_image}
                    className="rounded-lg w-full mb-6"
                  />
                  {showEbookBanner && (
                    <div
                      className="cursor-pointer md:hidden block mb-4"
                      onClick={handleEbookClick}
                    >
                      <img
                        src="https://content.qeeb.in/coltec/blogs/e-book-popup.webp"
                        alt="Download eBook"
                        className="slide-right rounded-md border"
                      />
                    </div>
                  )}
                  <div
                    className="prose md:mt-12 mt-6"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  ></div>
                  {blog.shop_now_image ? (
                    <Link to="/shop" target="_blank">
                      <img
                        className="rounded-md border"
                        alt={blog.shop_now_alt || "Demo Image"}
                        src={blog.shop_now_image}
                      />
                    </Link>
                  ) : null}
                  {blog.book_demo_image ? (
                    <img
                      onClick={() => setOpenBookDemoModal(true)}
                      className="rounded-md border cursor-pointer"
                      alt={blog.book_demo_alt || "Demo Image"}
                      src={blog.book_demo_image}
                    />
                  ) : null}
                </div>

                <div className="xl:col-span-4 col-span-full">
                  <div className="sticky top-[16rem]">
                    {showEbookBanner && (
                      <div
                        className="cursor-pointer md:block hidden mb-8"
                        onClick={handleEbookClick}
                      >
                        <img
                          src="https://content.qeeb.in/coltec/blogs/e-book-popup.webp"
                          alt="Download eBook"
                          className="slide-right rounded-md border"
                        />
                      </div>
                    )}
                    <h2 className="font-medium text-xl mb-3">Latest Blogs</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:block">
                      {latestTwoBlogs.map((latestBlog) => (
                        <div
                          key={latestBlog.id}
                          className="flex space-x-4 mb-6"
                        >
                          <div className="shrink-0">
                            <img
                              alt={latestBlog.blog_image_alt}
                              src={latestBlog.blog_image}
                              className="w-[150px] rounded-md h-[90px]"
                            />
                          </div>
                          <div>
                            <Link to={`/blogs/${latestBlog.slug}`}>
                              <h3 className="text-sm font-medium hover:underline">
                                {latestBlog.title}
                              </h3>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                    <h2 className="font-medium text-xl mt-8 mb-3">
                      Blog Categories
                    </h2>
                    <div className="flex flex-wrap gap-3">
                      <Link to="/blogs/interactive-flat-panel">
                        <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                          Interactive Flat Panel
                        </div>
                      </Link>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Classroom Platform
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Collaboration
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Conferencing
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Tablet
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Room Scheduler
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Kiosks
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Video Wall
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        Active LED
                      </div>
                      <div className="bg-[#F2F2F2] rounded-full py-[4px] px-3 text-[#242424] text-sm">
                        All-in-One Touch Monitor
                      </div>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-8 col-span-12">
                  <div className="flex space-x-4 items-center">
                    <button
                      onClick={handleLike}
                      disabled={isProcessingLike}
                      className={`flex flex-col items-center focus:outline-none ${
                        isLikeActive ? "cursor-pointer" : "cursor-pointer"
                      }`}
                      aria-pressed={isLikeActive}
                      aria-label="Like this blog"
                    >
                      <div className="flex space-x-1 items-center relative">
                        {isLikeActive ? (
                          <AiFillLike
                            className={`${
                              likeAnimation ? "like-button" : ""
                            } text-[22px] text-blue-500`}
                          />
                        ) : (
                          <AiOutlineLike className="text-[22px] text-gray-800" />
                        )}
                        <span className="text-gray-800 font-medium mt-0.5">
                          {likes}
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={handleDislike}
                      disabled={isProcessingDislike}
                      className={`flex flex-col items-center focus:outline-none ${
                        isDislikeActive
                          ? "cursor-pointer hidden"
                          : "cursor-pointer hidden"
                      }`}
                      aria-pressed={isDislikeActive}
                      aria-label="Dislike this blog"
                    >
                      <div className="flex items-center space-x-1">
                        {isDislikeActive ? (
                          <AiFillDislike
                            className={`${
                              dislikeAnimation ? "dislike-button" : ""
                            } text-[22px] text-red-500`}
                          />
                        ) : (
                          <AiOutlineDislike className="text-[22px] text-gray-800" />
                        )}
                        <span className="text-gray-800 font-medium">
                          {dislikes}
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={toggleComments}
                      className="flex flex-col items-center focus:outline-none cursor-pointer"
                      aria-pressed={showComments}
                      aria-label="Toggle comments section"
                    >
                      <div className="flex items-center space-x-1">
                        <AiOutlineComment className="text-[22px] text-gray-500 hover:text-gray-900" />
                        <span className="text-gray-800 font-medium">
                          {comments.length}
                        </span>
                      </div>
                    </button>
                    <div className="relative">
                      <button
                        onClick={toggleDropdown}
                        className="flex items-center space-x-2 text-gray-500 hover:text-black"
                      >
                        <FiShare className="text-[20px]" />
                      </button>
                      {isDropdownOpen && (
                        <div className="absolute top-10 right-0 left-0 bg-white border shadow-lg rounded-md w-48">
                          <button
                            onClick={handleCopyLink}
                            className="flex items-center text-left w-full px-4 py-3 text-sm text-gray-500 hover:text-gray-900"
                          >
                            <IoIosLink className="text-[16px] mr-1" />
                            Copy link
                          </button>
                          <div className="border-t border-gray-200"></div>
                          <button
                            onClick={handleShareOnX}
                            className="flex items-center text-left w-full px-4 py-3 text-sm text-gray-500 hover:text-gray-900"
                          >
                            <FaXTwitter className="text-[14px] mr-1" />
                            Share on X
                          </button>
                          <button
                            onClick={handleShareOnFacebook}
                            className="flex items-center text-left w-full px-4 py-3 text-sm text-gray-500 hover:text-gray-900"
                          >
                            <FaFacebook className="text-[16px] mr-1" />
                            Share on Facebook
                          </button>
                          <button
                            onClick={handleShareOnLinkedIn}
                            className="flex items-center text-left w-full px-4 py-3 text-sm text-gray-500 hover:text-gray-900"
                          >
                            <FaLinkedin className="text-[16px] mr-1" />
                            Share on LinkedIn
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {showComments && (
                    <div className="mt-8">
                      <h2 className="text-2xl font-medium mb-4">
                        Leave a Comment
                      </h2>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label
                            htmlFor="username"
                            className="block text-sm font-normal text-gray-800"
                          >
                            Name
                          </label>
                          {user ? (
                            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-gray-100">
                              {user.name}
                            </div>
                          ) : (
                            <input
                              type="text"
                              id="username"
                              name="username"
                              value={username}
                              onChange={(e) => setUsername(e.target.value)}
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Your name"
                            />
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="comment"
                            className="block text-sm font-normal text-gray-800"
                          >
                            Comment
                          </label>
                          <textarea
                            id="comment"
                            name="comment"
                            rows="4"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            required
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Your comment"
                          ></textarea>
                        </div>
                        <div>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`inline-flex mt-2 items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                              isSubmitting
                                ? "bg-gray-400"
                                : "bg-blue-500 hover:bg-blue-600"
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                          >
                            {isSubmitting ? "Submitting..." : "Post Comment"}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                  <div className="mt-8">
                    <h2 className="text-2xl font-medium mb-6">Comments</h2>
                    {isLoadingComments ? (
                      <p>Loading comments...</p>
                    ) : commentsError ? (
                      <p className="text-red-600">{commentsError}</p>
                    ) : comments.length === 0 ? (
                      <p>No comments yet. Be the first to comment!</p>
                    ) : (
                      <ul className="space-y-3">
                        {comments.map((cmt) => (
                          <li
                            key={cmt.commentId}
                            className="bg-neutral-50/80 p-4 rounded-lg"
                          >
                            <div className="flex items-center">
                              {user && user.avatar ? (
                                <img
                                  src={user.avatar}
                                  alt={`${cmt.username}'s avatar`}
                                  className="w-9 h-9 mr-3 rounded-full"
                                />
                              ) : (
                                <img
                                  src="https://content.qeeb.in/coltec/default-avatar.png"
                                  alt="Default Avatar"
                                  className="w-9 h-9 mr-3 rounded-full"
                                />
                              )}
                              <div>
                                <p className="text-base font-medium">
                                  {cmt.username}
                                </p>
                                <p className="text-gray-500 text-[11px]">
                                  {formatCommentDate(cmt.createdAt)}
                                </p>
                              </div>
                            </div>
                            <p className="text-gray-800 text-sm mt-3">
                              {cmt.comment}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {(prevBlog || nextBlog) && (
              <div className="px-6 lg:px-8 max-w-7xl mx-auto pb-14">
                <ul
                  className={`xl:flex space-y-4 xl:divide-x-2 xl:space-y-0 ${
                    prevBlog && nextBlog
                      ? "justify-between"
                      : prevBlog
                      ? "justify-start"
                      : nextBlog
                      ? "justify-end"
                      : ""
                  }`}
                >
                  {prevBlog && (
                    <Link to={`/blogs/${prevBlog.slug}`} className="group">
                      <li className="flex justify-start items-center">
                        <div className="mr-3">
                          <img
                            alt="blogs-arrow"
                            className="h-4"
                            src="https://content.qeeb.in/coltec/blogs-arrow-l.svg"
                          />
                        </div>
                        <div>
                          <p className="text-base font-light text-gray-700">
                            Previous
                          </p>
                          <p className="xl:w-[500px] text-base">
                            {prevBlog.title}
                          </p>
                        </div>
                      </li>
                    </Link>
                  )}
                  {nextBlog && (
                    <Link to={`/blogs/${nextBlog.slug}`} className="group pl-8">
                      <li className="flex justify-end items-center">
                        <div className="text-right">
                          <p className="text-base font-light text-gray-700">
                            Next
                          </p>
                          <p className="xl:w-[500px] text-base">
                            {nextBlog.title}
                          </p>
                        </div>
                        <div className="ml-3">
                          <img
                            alt="blogs-arrow"
                            className="h-4"
                            src="https://content.qeeb.in/coltec/blogs-arrow-r.svg"
                          />
                        </div>
                      </li>
                    </Link>
                  )}
                </ul>
              </div>
            )}
          </div>
          <CTA />
          <Footer />
          <ScrollToTopButton />
          <WhatsAppMsg />
          {openBookDemoModal && (
            <div className="fixed inset-0 mx-3 flex justify-center items-center z-50">
              <div
                className="fixed inset-0 bg-black opacity-50"
                onClick={() => setOpenBookDemoModal(false)}
              ></div>
              <div className="modal-landing-form rounded-lg shadow-lg z-10 relative w-full max-w-2xl mx-auto">
                <button
                  onClick={() => setOpenBookDemoModal(false)}
                  className="absolute top-0 right-0 m-4 text-gray-600 hover:text-gray-800"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
                <div>
                  <div className="max-w-2xl mx-auto px-8 pt-8">
                    <h2 className="relative justify-center text-center flex text-black md:text-2xl text-lg font-semibold">
                      <img
                        className="mt-[-5px] mr-2 h-4"
                        alt="form-qoute-1"
                        src="https://content.qeeb.in/coltec/landing/form-qoute-1.svg"
                      />
                      See it to Believe it
                      <img
                        className="mt-[10px] ml-2 h-4"
                        alt="form-qoute-2"
                        src="https://content.qeeb.in/coltec/landing/form-qoute-2.svg"
                      />
                    </h2>
                    <div className="mb-4 md:text-center">
                      <span className="text-black text-lg md:text-2xl font-semibold">
                        Request
                      </span>
                      <span className="text-[#7c50f5] md:text-2xl text-lg font-semibold">
                        {" "}
                      </span>
                      <span className="text-[#79adf3] md:text-2xl text-lg font-semibold">
                        Consultation{" "}
                      </span>
                      <span className="text-black md:text-2xl text-lg font-semibold">
                        Now
                      </span>
                    </div>
                  </div>
                  <div className="modal-iframe">
                    <iframe
                      src="/modal-landing-form.html"
                      title="outer-iframe"
                      className="w-full md:h-[390px] h-[475px]"
                      style={{ border: "none" }}
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <p>Blog not found</p>
      )}
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
      />
    </div>
  );
};

export default BlogDetails;
